import * as React from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

/**
 * Accessible icon-only button: every icon-only control must expose a
 * meaningful accessible name (aria-label) AND show a visible tooltip on
 * both mouse hover and keyboard focus (Radix Tooltip opens on focus).
 * A global <TooltipProvider> is mounted in App.tsx.
 *
 * Usage: <IconButton label="Delete rule" onClick={...}><Trash2 /></IconButton>
 */
export interface IconButtonProps extends Omit<ButtonProps, "size" | "aria-label"> {
  /** Accessible name announced by screen readers and shown as the tooltip. */
  label: string;
  size?: ButtonProps["size"];
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, size = "icon", children, ...props }, ref) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button ref={ref} type="button" size={size} aria-label={label} {...props}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  ),
);
IconButton.displayName = "IconButton";
